import Container from "components/shared/Container";
import Icon from "components/shared/Icon";
import Link from "components/shared/Link";
import { useContext, useEffect, useState } from "react";
import { getCookie, setCookie } from "utils/cookie";
import { Context } from "Context";
import useFetch from "hooks/useFetch";
import appConfig from "config.json";
import { isTravelAgent } from "utils";

const classTogglesCookieKey = "classToggles_jar";
const travelAgentHeaderMainClass = "syndicated-travel-agent-header";
const travelAgentHeaderInformationClass = `${travelAgentHeaderMainClass}__agent-information`;
const syndicatedHeaderMainClass = "syndicated-header";
const canadaStoreSuffix = "-ca";
const guestSensitiveClass = "guestSensitive";
const travelTradeExpandedClass = "navigationTravelAgentHeaderCollapsed";

const processTravelAgentInfoLink = (url) => {
	const title = url.replace(/http:\/\/|https:\/\//, "");
	return {
		href: url,
		title: title?.charAt(title.length - 1) === "/" ? title.slice(0, -1) : title,
	};
};

const removeSymbolsFromText = (text) => {
	if (text) {
		return text.replace(/\+/g, " ");
	}
	return "";
};

const joinTexts = (firstText, secondText) => {
	return removeSymbolsFromText(
		`${firstText || ""}${firstText && secondText ? " " : ""}${secondText || ""}`
	);
};

const getClassTogglesCookieValue = () => {
	const currentCookieValue = getCookie(classTogglesCookieKey);
	if (currentCookieValue) {
		try {
			const parsedValue = JSON.parse(decodeURIComponent(currentCookieValue));
			if (parsedValue && typeof parsedValue === "object") {
				return parsedValue;
			}
		} catch (error) {
			console.error(error);
		}
	}
	return { travelAgentInfoHidden: false };
};

const setClassTogglesCookie = (travelAgentInfoHidden) => {
	const classTogglesCookieValue = getClassTogglesCookieValue() || {};
	try {
		setCookie(
			classTogglesCookieKey,
			encodeURIComponent(
				JSON.stringify({ ...classTogglesCookieValue, travelAgentInfoHidden })
			),
			365
		);
	} catch (error) {
		console.error(error);
	}
};

const getTravelTradeDetailsToShow = (
	label = "",
	value = "",
	validateValue = false
) => {
	if (validateValue && !value) return "";
	if (label && !label.includes(" ")) {
		label = label.toUpperCase();
	}
	return `${label || ""}${label ? ": " : ""}${value}`;
};

const TravelAgentHeader = ({ isolated = false }) => {
	const {
		headerData,
		headerData: { chrome },
		setHeaderData,
		profileData: { travelAgent, userType },
		config: { store, brand },
	} = useContext(Context);
	const [chromeUrl, setChromeUrl] = useState("");

	const [expand, setExpand] = useState(
		!Boolean(getClassTogglesCookieValue()?.travelAgentInfoHidden)
	);
	const [travelAgentInfoLink, setTravelAgentInfoLink] = useState(null);
	const [showTravelAgentInfo, setShowTravelAgentInfo] = useState(false);

	const { brands, content } = appConfig;
	const brandConfig = brands?.[brand];
	const isUserTypeRequired = brandConfig?.isUserTypeRequiredForContentRequest;
	const { response, error } = useFetch(chromeUrl, {}, 0, chromeUrl);

	useEffect(() => {
		if (showTravelAgentInfo) {
			setClassTogglesCookie(!expand);
			document.dispatchEvent(
				new CustomEvent("navigationTravelAgentInfo", { detail: expand })
			);
		}
		if (expand) {
			document.body.classList.remove(travelTradeExpandedClass);
		} else {
			document.body.classList.add(travelTradeExpandedClass);
		}
	}, [expand, showTravelAgentInfo]);

	useEffect(() => {
		if (
			(travelAgent?.firstName || travelAgent?.travelAgency?.partnerId) &&
			(store || (isUserTypeRequired && isTravelAgent(userType)))
		) {
			setShowTravelAgentInfo(true);
		}
	}, [travelAgent, store, isUserTypeRequired, userType]);

	useEffect(() => {
		if (travelAgent?.travelAgency?.partnerId) {
			var id = travelAgent.travelAgency.partnerId;
			if (!window.PEP) {
				window.PEP = {
					Config: { 
						partnerId: id,
					},
				};
			} else if (!window.PEP.Config) {
				window.PEP.Config = {
					partnerId: id,
				};
			} else if (!window.PEP.Config.partnerId) {
				window.PEP.Config.partnerId = id;
			}
		}
	}, [travelAgent]);

	useEffect(() => {
		if (isolated) {
			setChromeUrl(content.apiUrl.chrome);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isolated]);

	useEffect(() => {
		if (!chrome && response) {
			setHeaderData({ ...headerData, chrome: response, loading: false });
		} else if (error) {
			setHeaderData({ ...headerData, error: true, loading: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error, response, chrome]);

	useEffect(() => {
		if (chrome) {
			if (
				chrome.travelAgentInfoLinkCA &&
				store?.substring(store?.length - canadaStoreSuffix.length) ===
					canadaStoreSuffix
			) {
				setTravelAgentInfoLink(chrome.travelAgentInfoLinkCA);
			} else if (chrome.travelAgentInfoLink) {
				setTravelAgentInfoLink(chrome.travelAgentInfoLink);
			} else if (!chrome.travelAgentInfoLink && travelAgent.returnUrl) {
				setTravelAgentInfoLink(
					processTravelAgentInfoLink(travelAgent.returnUrl)
				);
			}
		}
	}, [store, travelAgent, chrome]);

	return showTravelAgentInfo ? (
		<div
			className={`${syndicatedHeaderMainClass} ${syndicatedHeaderMainClass}--${brand}`}
			style={{visibility: "hidden"}}
		>
			<div
				className={`${travelAgentHeaderMainClass} ${travelAgentHeaderMainClass}--${brand}`}
			>
				<Container direction="column">
					<div
						className={`${travelAgentHeaderInformationClass} ${travelAgentHeaderInformationClass}--${
							expand ? "expanded" : " collapsed"
						}`}
					>
						<div className={`${travelAgentHeaderInformationClass}__main`}>
							<h3 className={`${guestSensitiveClass}High`}>
								{removeSymbolsFromText(
									`${travelAgent.firstName || ""} ${travelAgent.lastName || ""}`
								)}
							</h3>
							<p className={guestSensitiveClass}>
								{removeSymbolsFromText(travelAgent.travelAgency?.name)}
							</p>
						</div>
						<div className={`${travelAgentHeaderInformationClass}__details`}>
							<p className={guestSensitiveClass}>
								{[
									joinTexts(
										travelAgent.travelAgency?.address?.line1,
										travelAgent.travelAgency?.address?.line2
									),
									travelAgent.travelAgency?.address?.city,
									travelAgent.travelAgency?.address?.stateOrProvince,
									travelAgent.travelAgency?.address?.postalCode,
								]
									.filter((item) => item)
									.join(", ")}
							</p>
							<p>
								<span className={guestSensitiveClass}>
									{getTravelTradeDetailsToShow(
										chrome?.phoneLabel?.toUpperCase() || "PHONE",
										travelAgent.travelAgency?.phone,
										true
									)}
								</span>
								<span>
									{travelAgent.travelAgency?.phone ? " | " : ""}
									{getTravelTradeDetailsToShow(
										chrome?.partnerIdTypeLabel ||
											travelAgent.travelAgency?.partnerIdType ||
											"",
										travelAgent.travelAgency?.partnerId
									)}
								</span>
							</p>
						</div>
						<div className={`${travelAgentHeaderInformationClass}__link`}>
							{travelAgentInfoLink && <Link {...travelAgentInfoLink} />}
						</div>
					</div>
					<button
						className={`${travelAgentHeaderMainClass}__button${
							expand ? ` ${travelAgentHeaderMainClass}__button--expanded` : ""
						}`}
						onClick={() => setExpand(!expand)}
						tabIndex={0}
					>
						{expand ? chrome?.hideTravelAgentInfo : chrome?.showTravelAgentInfo}
						<Icon
							icon="icon_expand-show-more"
							className={expand ? "syndicated-icon--rotate-180" : ""}
						/>
					</button>
				</Container>
			</div>
		</div>
	) : null;
};

export default TravelAgentHeader;
